import { memo, useEffect, useRef, useState } from "react";
import { ArrowUp, Mic, Paperclip, Square, X } from "lucide-react";
import { Button, Kbd } from "@/components/ui";
import { useChat, useHotkey, useVoice } from "@/hooks";
import { useAppStore, useConversationsStore } from "@/stores";
import { cn } from "@/utils";
import { fileToAttachment, isImageFile } from "@/services/ai/vision/imageAttachments";
import type { ChatImageAttachment } from "@/types";

/**
 * InputBar
 *
 * The bottom composer for the Chat area. Submits through `useChat`, which
 * handles creating a conversation on first send, streaming the reply, and
 * persisting everything — this component only owns the textarea's own local
 * draft state.
 */
export const InputBar = memo(function InputBar() {
  const [value, setValue] = useState("");
  const [pendingImages, setPendingImages] = useState<ChatImageAttachment[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const activeConversationId = useConversationsStore((s) => s.activeConversationId);
  const { sendMessage, stop, isGenerating } = useChat(activeConversationId);
  const { isRecording, startRecording, stopRecording } = useVoice();
  const canSend = (value.trim().length > 0 || pendingImages.length > 0) && !isGenerating;

  const pendingComposerText = useAppStore((s) => s.pendingComposerText);
  useEffect(() => {
    if (pendingComposerText === null) return;
    setValue((prev) => (prev ? `${prev}\n${pendingComposerText}` : pendingComposerText));
    useAppStore.getState().setPendingComposerText(null);
    requestAnimationFrame(() => {
      ref.current?.focus();
      grow();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `grow` reads `ref` fresh each call; including it would re-run on every render.
  }, [pendingComposerText]);

  function grow() {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }

  async function addImageFiles(files: FileList | File[]) {
    const images = Array.from(files).filter(isImageFile);
    if (images.length === 0) return;
    const attachments = await Promise.all(images.map(fileToAttachment));
    setPendingImages((prev) => [...prev, ...attachments]);
  }

  function submit() {
    if (!canSend) return;
    sendMessage(value, pendingImages);
    setValue("");
    setPendingImages([]);
    requestAnimationFrame(grow);
  }

  useHotkey("mod+enter", submit);
  useHotkey("escape", stop, { enabled: isGenerating });

  return (
    <div className="shrink-0 border-t border-border bg-bg-base px-4 py-3">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) void addImageFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <div className="mx-auto max-w-3xl">
        {pendingImages.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {pendingImages.map((img) => (
              <div key={img.id} className="group relative h-14 w-14 overflow-hidden rounded-md border border-border">
                <img
                  src={`data:${img.mimeType};base64,${img.base64}`}
                  alt="Attached"
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  aria-label="Remove attachment"
                  onClick={() => setPendingImages((prev) => prev.filter((p) => p.id !== img.id))}
                  className="absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-bg-base/80 text-text-primary opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          void addImageFiles(e.dataTransfer.files);
        }}
        className={cn(
          "mx-auto flex max-w-3xl items-end gap-2 rounded-lg border border-border bg-bg-elevated",
          "px-3 py-2 transition-colors duration-fast focus-within:border-border-strong",
          isDragOver && "border-border-strong",
        )}
      >
        <button
          type="button"
          aria-label="Attach image"
          onClick={() => fileInputRef.current?.click()}
          className="mb-0.5 flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary transition-colors hover:bg-bg-hover hover:text-text-primary"
        >
          <Paperclip size={15} strokeWidth={1.75} />
        </button>

        <button
          type="button"
          aria-label={isRecording ? "Stop recording" : "Record a voice message (push-to-talk)"}
          onMouseDown={() => void startRecording()}
          onMouseUp={stopRecording}
          onMouseLeave={() => isRecording && stopRecording()}
          className={cn(
            "mb-0.5 flex h-7 w-7 items-center justify-center rounded-md transition-colors",
            isRecording
              ? "bg-bg-hover text-text-primary animate-pulse"
              : "text-text-tertiary hover:bg-bg-hover hover:text-text-primary",
          )}
        >
          <Mic size={15} strokeWidth={1.75} />
        </button>

        <textarea
          ref={ref}
          value={value}
          rows={1}
          placeholder="Message Seufic AI…"
          aria-label="Message input"
          onChange={(e) => {
            setValue(e.target.value);
            grow();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData.files);
            if (files.length > 0) void addImageFiles(files);
          }}
          className="selectable max-h-40 flex-1 resize-none bg-transparent py-1 text-base text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />

        {isGenerating ? (
          <Button
            variant="secondary"
            size="sm"
            className="mb-0.5 h-7 w-7 p-0"
            onClick={stop}
            aria-label="Stop generating"
          >
            <Square size={11} fill="currentColor" />
          </Button>
        ) : (
          <Button
            variant="primary"
            size="sm"
            className="mb-0.5 h-7 w-7 p-0"
            onClick={submit}
            disabled={!canSend}
            aria-label="Send message"
          >
            <ArrowUp size={15} strokeWidth={2} />
          </Button>
        )}
      </div>
      <p className="mx-auto mt-2 max-w-3xl text-center text-2xs text-text-faint">
        Press <Kbd>↵</Kbd> to send · <Kbd>⇧ ↵</Kbd> for a new line
        {isGenerating && (
          <>
            {" "}
            · <Kbd>esc</Kbd> to stop
          </>
        )}
      </p>
    </div>
  );
});
